import React, { useEffect, useState, useRef } from "react"
import PropTypes from "prop-types"
import MetaTags from "react-meta-tags"
import { connect } from "react-redux"
import { withRouter, Link } from "react-router-dom"
import {
  Form,
  Input,
  Button,
  Card,
  Alert,
  Row,
  Col,
  Typography,
  Space,
  Divider,
  Progress,
} from "antd"
import {
  SafetyCertificateOutlined,
  MobileOutlined,
  ArrowRightOutlined,
  LockOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons"

import { loginUserByCode, resetErrorLogin } from "../../store/actions"
import { persianToEnglishNumbers } from "../../helpers/utilities"
import logoSm from "../../assets/images/nazamlogo.png"
import "./auth.scss"

const { Title, Text, Paragraph } = Typography

const SubmitSmsPage = ({ error, loading, loginUserByCode, resetErrorLogin, history, location }) => {
  const [form] = Form.useForm()
  const [countdown, setCountdown] = useState(120)
  const [canResend, setCanResend] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        resetErrorLogin()
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [error, resetErrorLogin])

  // Focus input on load
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [countdown])

  const handleSubmit = values => {
    const query = new URLSearchParams(location.search)
    const userName = query.get("username")
    const normalizedCode = persianToEnglishNumbers(values.code)
    loginUserByCode({ ...values, code: normalizedCode, userName }, history)
  }

  const handleCodeChange = e => {
    const code = e.target.value
    if (code.length === 6) {
      form.submit()
    }
  }

  const handleResendCode = () => {
    setCountdown(120)
    setCanResend(false)
    // TODO: Call resend API
  }

  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  return (
    <div className="auth-container">
      <MetaTags>
        <title>تایید دوعاملی | واحد گاز</title>
      </MetaTags>

      <div className="auth-background">
        <div className="auth-gradient-circle circle-1" />
        <div className="auth-gradient-circle circle-2" />
        <div className="auth-gradient-circle circle-3" />
      </div>

      <Row justify="center" align="middle" className="auth-row">
        <Col xs={22} sm={18} md={14} lg={10} xl={8} xxl={6}>
          <Card className="auth-card" bordered={false}>
            {/* Header */}
            <div className="auth-header">
              <div className="auth-icon-wrapper">
                <SafetyCertificateOutlined className="auth-main-icon" />
              </div>
              <Title level={3} className="auth-title">
                تایید هویت دو مرحله‌ای
              </Title>
              <Text type="secondary" className="auth-subtitle">
                کد تایید به موبایل شما ارسال شد
              </Text>
            </div>

            {/* Mobile Animation */}
            <div className="sms-animation">
              <div className="phone-icon-container">
                <MobileOutlined className="phone-icon" />
                <div className="sms-waves">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>

            <Divider />

            {/* Form */}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              size="large"
            >
              {error && typeof error === "string" && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  closable
                  className="auth-alert"
                />
              )}

              <Form.Item
                name="code"
                rules={[
                  { required: true, message: "لطفاً کد تایید را وارد کنید" },
                  { len: 6, message: "کد تایید باید 6 رقم باشد" },
                ]}
              >
                <Input
                  ref={inputRef}
                  prefix={<LockOutlined className="input-icon" />}
                  placeholder="کد 6 رقمی"
                  maxLength={6}
                  className="otp-input"
                  autoComplete="one-time-code"
                  onChange={handleCodeChange}
                />
              </Form.Item>

              {/* Timer */}
              <div className="timer-section">
                {!canResend ? (
                  <Space direction="vertical" align="center" style={{ width: "100%" }}>
                    <Progress
                      type="circle"
                      percent={(countdown / 120) * 100}
                      format={() => formatTime(countdown)}
                      size={80}
                      strokeColor={{
                        "0%": "#1890ff",
                        "100%": "#52c41a",
                      }}
                    />
                    <Text type="secondary">زمان باقیمانده برای ارسال مجدد</Text>
                  </Space>
                ) : (
                  <Button
                    type="dashed"
                    icon={<ReloadOutlined />}
                    onClick={handleResendCode}
                    block
                  >
                    ارسال مجدد کد
                  </Button>
                )}
              </div>

              <Form.Item className="submit-section">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<CheckCircleOutlined />}
                  block
                  className="auth-submit-btn"
                >
                  {loading ? "در حال بررسی..." : "تایید و ورود"}
                </Button>
              </Form.Item>
            </Form>

            {/* Footer */}
            <div className="auth-footer">
              <Link to="/login" className="back-link">
                <ArrowRightOutlined />
                <span>بازگشت به صفحه ورود</span>
              </Link>
            </div>

            <Divider className="footer-divider" />

            {/* Logo and Copyright */}
            <div className="auth-brand">
              <img src={logoSm} alt="Logo" className="auth-logo" />
              <Text type="secondary" className="copyright">
                © {new Date().getFullYear()} واحد گاز کردستان
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

SubmitSmsPage.propTypes = {
  error: PropTypes.any,
  history: PropTypes.object,
  location: PropTypes.object,
  loginUserByCode: PropTypes.func,
  loading: PropTypes.any,
  resetErrorLogin: PropTypes.func,
}

const mapStateToProps = state => {
  const { error, loading } = state.Login
  return { error, loading }
}

export default withRouter(
  connect(mapStateToProps, { loginUserByCode, resetErrorLogin })(SubmitSmsPage)
)
